import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { Overline } from "@/components/motion";
import { getTestimonials } from "@/lib/api";

export default function HomeTestimonials({ title, subtitle }) {
  const [items, setItems] = useState([]);
  const [i, setI] = useState(0);

  useEffect(() => {
    getTestimonials({ featured: true }).then((d) => setItems(d.length ? d : [])).catch(() => {});
  }, []);

  if (!items.length) return null;
  const t = items[i % items.length];
  const next = () => setI((p) => (p + 1) % items.length);
  const prev = () => setI((p) => (p - 1 + items.length) % items.length);

  return (
    <section id="testimonials" data-testid="home-testimonials" className="relative py-24 sm:py-32 bg-[#fdfbf7] overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <Overline>{title || "Guest Stories"}</Overline>
            <h2 className="font-serif text-[#3b1a1a] text-4xl sm:text-6xl leading-[1.03] tracking-tight mt-5">
              {subtitle
                ? <>Loved by <span className="italic text-[#7c1d1d]">{subtitle.replace(/^From\s+/i, "")}.</span></>
                : <>Loved by travellers <span className="italic text-[#7c1d1d]">around the world.</span></>}
            </h2>
            <div className="relative mt-10 w-full max-w-xs">
              <AnimatePresence mode="wait">
                <motion.div key={t.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.5 }}
                  className="relative rounded-t-[130px] border border-[#d4af37]/50 bg-[#14523d] text-white aspect-[4/5] flex flex-col items-center justify-center text-center px-6 overflow-hidden">
                  <div className="absolute inset-0 paper-grain opacity-20" />
                  <span className="relative text-6xl">{t.country_flag}</span>
                  <div className="relative font-serif text-3xl mt-4 leading-none">{t.name}</div>
                  <div className="relative text-[11px] tracking-[0.24em] uppercase text-[#e8c970] mt-3">{t.country}</div>
                  <div className="relative flex items-center gap-1 text-[#d4af37] mt-4">
                    {Array.from({ length: t.rating || 5 }).map((_, k) => <Star key={k} className="w-3.5 h-3.5 fill-[#d4af37]" strokeWidth={0} />)}
                  </div>
                </motion.div>
              </AnimatePresence>
              <div className="absolute -inset-3 rounded-t-[150px] border border-[#d4af37]/30 pointer-events-none" />
            </div>
          </div>

          <div className="lg:col-span-7">
            <Quote className="w-14 h-14 text-[#d4af37] mb-6" strokeWidth={1.2} />
            <AnimatePresence mode="wait">
              <motion.blockquote key={t.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.5 }}
                data-testid="testimonial-quote" className="font-serif italic text-2xl sm:text-3xl lg:text-[2.5rem] leading-snug text-[#3b1a1a]">
                “{t.review}”
              </motion.blockquote>
            </AnimatePresence>
            <div className="mt-8 flex items-center gap-1 text-[#d4af37]">
              {Array.from({ length: t.rating || 5 }).map((_, k) => <Star key={k} className="w-4 h-4 fill-[#d4af37]" strokeWidth={0} />)}
            </div>
            <div className="mt-3 flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="font-serif text-xl text-[#3b1a1a]">{t.country_flag} {t.name}</div>
                <div className="text-[11px] tracking-[0.24em] uppercase text-[#5a4b4b] mt-1">{t.country}</div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={prev} data-testid="testimonial-prev" aria-label="Previous" className="w-12 h-12 border border-[#7c1d1d]/40 hover:bg-[#7c1d1d] hover:text-white transition-colors grid place-items-center"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={next} data-testid="testimonial-next" aria-label="Next" className="w-12 h-12 border border-[#7c1d1d]/40 hover:bg-[#7c1d1d] hover:text-white transition-colors grid place-items-center"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
